import { IAnalyticsAdapter } from "./IAnalyticsAdapter";
import { ServiceType } from "../ServiceManager";
import { AnalyticsEvent, AnalyticsEventPriority, AnalyticsSessionReferrerType } from "./AnalyticsEvents";
import { ClientWebsiteConfig } from "../Config";
import { Logger, LogLevel } from "../Logger";
import { AsyncQueue, AsyncQueueDelegate } from "../../util/AsyncQueue";
import { Eventable } from "../../util";

enum PROListAnalyticsCommandType {
  Setup = 'pla.configure',
  RecordEvents = 'pla.flush',
  SetContactId = 'pla.set_contact_id',
  SetFCMToken = 'pla.set_fcm_token',

  ClientRegistered = 'pla.client_registered'
}

interface PROListAnalyticsClientData {
  ClientId: string;
  ContactId?: string;
}

class PROListAnalyticsAdapter extends Eventable implements IAnalyticsAdapter, AsyncQueueDelegate<AnalyticsEvent> {
    Type: ServiceType = ServiceType.Analytics;
    Id: string = 'PROListAnalyticsAdapter';
    private WebsiteId: string;
    private Frame: HTMLIFrameElement;
    private Ready: boolean = false;
    private Queue: AsyncQueue<AnalyticsEvent> = new AsyncQueue<AnalyticsEvent>(2000, this);
    private readonly Origin = window.location.origin;
  
    constructor(config: ClientWebsiteConfig) {
      super();

      this.WebsiteId = config.Settings!.Analytics.PROListAnalyticsWebsiteId;

      this.Frame = document.createElement('iframe');
      this.Frame.style.width = '1px';
      this.Frame.style.height = '1px';
      this.Frame.style.opacity = '0';
      this.Frame.style.position = 'absolute';
      this.Frame.style.top = '0px';
      this.Frame.style.left = '0px';

      document.body.appendChild(this.Frame);

      this.Frame.onload = () => {
        Logger.Log(LogLevel.Debug, 'Initialised PROLIST Analaytics');

        this.SetupListener();

        const query: Record<string, string> = {};
        window.location.search.substr(1).split('&').forEach((itm) => {
            const parts = itm.split('=');
  
            if (parts.length === 2) {
              query[decodeURIComponent(parts[0])] = decodeURIComponent(parts[1]);
            }
        });

        let refType = AnalyticsSessionReferrerType.None;
        let refId = '';

        if (query["EmailCampaignId"]) {
          refType = AnalyticsSessionReferrerType.EmailCampaign;
          refId = query["EmailCampaignId"];
        }

        this.Dispatch(PROListAnalyticsCommandType.Setup, {
          origin: this.Origin,
          referrerType: refType,
          referrerId: refId,
        });

        this.On(PROListAnalyticsCommandType.ClientRegistered, () => {
          this.Ready = true;

          if (query["ContactId"]) {
            this.SetContactId(query["ContactId"]);
          }

          this.Queue.DoFlush();
        });
      };

      this.Frame.onerror = () => {
        Logger.Log(LogLevel.Error, 'Failed to initialise PROLIST Analytics');
      };

      this.Frame.src = `https://analytics.prolist.net.au/collector.html?ClientWebsiteId=${config.Id}&Origin=${window.location.origin}&_=${new Date().getTime()}`;
    }

    Record(event: AnalyticsEvent): void {
      this.Queue.Push(event);

      if (event.Priority === AnalyticsEventPriority.Realtime) this.Queue.DoFlush();
    }

    Flush(queue: AnalyticsEvent[]): Promise<boolean> {
      return new Promise((resolve, reject) => {
        Logger.Log(LogLevel.Debug, '[PROLIST Analytics]', 'Flushing queue', queue);

        if(this.Ready) {
          this.Dispatch(PROListAnalyticsCommandType.RecordEvents, {
            events: queue
          });

          resolve(true);
        } else {
          resolve(false);
        }
      });
    }

    SetContactId(contactId: string) {
      this.Dispatch(PROListAnalyticsCommandType.SetContactId, contactId);
    }

    SetFCMToken(fcmToken: string) {
      this.Dispatch(PROListAnalyticsCommandType.SetFCMToken, fcmToken);
    }

    private SetupListener() {
      window.addEventListener('message', (event) => {
        if (event.origin !== 'https://analytics.prolist.net.au') return;

        try {
          this.Trigger(event.data.command, event.data.data);
        } catch(ex) {
          Logger.Log(LogLevel.Warning, '[PROLIST Analytics]', 'Failed to handle message', ex, event);
        }
      }, false);
    }

    private Dispatch(command: PROListAnalyticsCommandType, message?: any) {
      this.Frame.contentWindow?.postMessage({
        command: command,
        payload: message
      }, 'https://analytics.prolist.net.au');
    }
}

export { PROListAnalyticsAdapter, PROListAnalyticsCommandType, PROListAnalyticsClientData }
